import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Reveal from '@/components/Reveal';
import ServiceDeliverables from '@/components/ServiceDeliverables';
import ServicePricing from '@/components/ServicePricing';
import { Image } from '@/components/ui/image';
import { SERVICES, getService } from '@/data/services';
import { CONTACT } from '@/data/contact';

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = getService(slug);
  const other = SERVICES.find((x) => x.slug !== slug);
  const contactEmail = slug === 'digital-production' ? CONTACT.sales : CONTACT.bookings;

  if (!s) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Navbar />
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 pt-40 pb-24 text-center">
          <h1 className="font-display font-bold text-3xl text-white">Service not found</h1>
          <Link to="/#cores" className="mt-6 inline-flex items-center gap-2 text-[#3B82F6] hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to our divisions
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Navbar />

      <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 fine-grid opacity-60" />
        <div className="absolute top-0 right-0 w-2/3 h-full bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.16),transparent_58%)]" />

        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
          <Link to="/#cores" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#3B82F6] transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> Our Cores & Functions
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6] block mb-5"
          >
            {s.eyebrow}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-bold text-white tracking-tight leading-[1.05] max-w-4xl"
            style={{ fontSize: 'clamp(2.2rem, 5vw, 4.2rem)' }}
          >
            {s.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-6 text-white/60 text-lg lg:text-xl font-light leading-relaxed max-w-2xl"
          >
            {s.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href={`mailto:${contactEmail}?subject=Enquiry — ${s.title}`}
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors blue-glow"
            >
              Request a Proposal
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/#contact"
              className="inline-flex items-center justify-center px-7 py-3.5 rounded-lg text-sm font-semibold border border-white/20 text-white hover:border-white/50 transition-colors"
            >
              Contact the Team
            </Link>
          </motion.div>
          </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="relative rounded-lg overflow-hidden border border-white/10 aspect-[4/5]">
                <Image src={s.img} alt={s.title} className="w-full h-full" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] mb-2">Ahana Digital</p>
                  <p className="text-white font-display font-semibold text-lg leading-snug">{s.eyebrow}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10 bg-white/[0.02]">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {s.stats.map((st, i) => (
            <Reveal key={st.v} delay={i * 0.07}>
              <div>
                <div className="font-display font-bold text-2xl lg:text-3xl text-white">{st.k}</div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-white/45 mt-1.5 leading-tight">{st.v}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INTRO + CAPABILITIES */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal className="max-w-3xl mb-16">
            <h2 className="font-display font-bold text-[#0B0B0B] tracking-tight text-3xl lg:text-4xl leading-tight">
              How this division operates
            </h2>
            <p className="mt-6 text-[#0B0B0B]/65 text-base lg:text-lg font-light leading-relaxed">{s.intro}</p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {s.capabilities.map((c, i) => (
              <Reveal key={c.title} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-lg border border-[#0B0B0B]/10 bg-[#F5F5F5] p-7 hover:bg-white hover:border-[#2563EB] hover:shadow-[0_20px_60px_-30px_rgba(37,99,235,0.4)] transition-all duration-500">
                  <div className="w-9 h-9 rounded-lg bg-[#2563EB]/10 border border-[#2563EB]/25 flex items-center justify-center mb-5">
                    <Check className="w-4 h-4 text-[#2563EB]" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-[#0B0B0B] mb-3 leading-snug">{c.title}</h3>
                  <p className="text-sm text-[#0B0B0B]/60 leading-relaxed font-light">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ServiceDeliverables deliverables={s.deliverables} idealFor={s.idealFor} />

      {/* PROCESS */}
      <section className="bg-white border-t border-[#0B0B0B]/10 py-20 lg:py-28">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2563EB] mb-4 block">
              Engagement Process
            </span>
            <h2 className="font-display font-bold text-[#0B0B0B] tracking-tight text-3xl lg:text-4xl">
              From brief to delivery
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {s.process.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div className="h-full rounded-lg bg-[#F5F5F5] border border-[#0B0B0B]/10 p-6">
                  <span className="font-display font-bold text-2xl text-[#2563EB]/25">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-display font-semibold text-base text-[#0B0B0B] mt-3">{p.step}</h3>
                  <p className="text-sm text-[#0B0B0B]/60 mt-2.5 leading-relaxed font-light">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ServicePricing tiers={s.pricing.tiers} note={s.pricing.note} serviceTitle={s.title} contactEmail={contactEmail} />

      {/* CROSS-LINK */}
      {other && (
        <section className="bg-[#0B0B0B] py-20 lg:py-24">
          <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
            <Reveal>
              <Link
                to={`/services/${other.slug}`}
                className="group block rounded-lg border border-white/10 bg-white/[0.03] p-10 lg:p-14 hover:border-[#3B82F6]/60 transition-all duration-500"
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6] block mb-4">
                  {other.eyebrow}
                </span>
                <h2 className="font-display font-bold text-white tracking-tight text-2xl lg:text-4xl max-w-2xl">
                  {other.title}
                </h2>
                <p className="mt-5 text-white/55 font-light leading-relaxed max-w-2xl">{other.tagline}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-white">
                  Explore this division
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}